import { useTranslation } from "react-i18next";
import { List } from "@mui/material";
import statusService from "api/statusService";
import tagsService from "api/tagsService";
import PackageSkeleton from "components/PackageSkeleton";
import {
  EOrder,
  ETypeOfOrder,
  IOrderObject,
} from "components/SortOrder/typings";
import KeysContext from "contexts/KeysContext";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import styles from "./styles.module.scss";
import NotFoundScreen from "components/NotFoundScreen";
import { useInfiniteQuery } from "react-query";
import { axiosInstance } from "api/axios";
import { IIssue } from "dto/Helpdesk/IIssue";
import ChatCard from "components/ChatCard";
import ChatsContext from "contexts/ChatsContext";
import SortOrder from "components/SortOrder";
import { IKey } from "dto/IKey";
import { SignalRContext } from "../../App";
import { HubCallbacksNames } from "api/hub";

export const ChatList = ({
  debouncedText,
  searchText,
}: {
  debouncedText: string;
  searchText: string;
}) => {
  const { t } = useTranslation();
  const { chats, setChats } = useContext(ChatsContext);

  SignalRContext.useSignalREffect(
    HubCallbacksNames.Online,
    (message) => {
      const oldList = [...chats];

      const toUpdate = oldList.filter((x) =>
        x.Participants.find((p) => p.UserId === message.userId)
      );

      toUpdate.forEach((t) => {
        const part = t.Participants.find((x) => x.UserId === message.userId);
        part.IsOnline = true;
      });

      setChats(oldList);
    },
    []
  );

  SignalRContext.useSignalREffect(
    HubCallbacksNames.Offline,
    (message) => {
      const oldList = [...chats];

      const toUpdate = oldList.filter((x) =>
        x.Participants.find((p) => p.UserId === message.userId)
      );

      toUpdate.forEach((t) => {
        const part = t.Participants.find((x) => x.UserId === message.userId);
        part.IsOnline = false;
      });

      setChats(oldList);
    },
    []
  );

  const [listFilter, setListFilter] = useState<IOrderObject>({
    type: ETypeOfOrder.LAST_MESSAGE,
    order: EOrder.DESCENDING,
  });

  const [selectedElements, setSelectedElements] = useState<IIssue[]>([]);

  const scrollWrapper = useRef(null);

  const [lastFetchTime, setLastFetchTime] = useState(0);
  const fetchThreshold = 1000; // 1 second

  const { keys, setKeys } = useContext(KeysContext);

  const { data: tagsData } = tagsService.useGetTags();
  const { data: statusesData } = statusService.useGetStatuses();

  const statuses = useMemo(() => statusesData?.data ?? [], [statusesData]);
  const tags = useMemo(() => tagsData?.data ?? [], [tagsData]);
  const separatorRef = useRef();
  const [isInitialized, setIsInitialized] = useState(false);

  const {
    data,
    fetchNextPage,
    isLoading,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery(
    ["chats", debouncedText, listFilter],
    async ({ pageParam = "" }) => {
      const query = new URLSearchParams(window.location.search);

      query.append("Page", pageParam !== "" ? pageParam : "0");
      query.append("SortProp", listFilter.type);
      query.append("SortOrder", listFilter.order);

      if (searchText.length) {
        query.append("Text", debouncedText);
      }

      const res = await axiosInstance.get(`/chat/list?${query}`);
      return res.data;
    },
    {
      enabled: false,
      staleTime: 0,
      cacheTime: 0,
      getNextPageParam: (lastPage) => {
        if (!lastPage.LastPage) {
          return lastPage.Page + 1;
        }
        return undefined;
      },
    }
  );

  const handleLoadNextPage = () => {
    if (!separatorRef.current || isLoading) return;

    const rect = separatorRef.current.getBoundingClientRect();

    const isSeparatorVisible =
      rect.top * 0.5 < window.innerHeight && rect.bottom >= 0;

    const currentTime = Date.now();
    if (currentTime - lastFetchTime < fetchThreshold) return;

    if (isSeparatorVisible) {
      setLastFetchTime(currentTime);
      fetchNextPage();
    } else if (scrollWrapper.current.scrollTop < 10) {
      //setLastFetchTime(currentTime);
      //fetchPrevPage();
    }
  };

  const compileData = async () => {
    if (!data) return;

    const packs = await Promise.all(
      data?.pages
        .map((page) => {
          return page.Items;
        })
        .flat()
    );

    const newKeys = packs
      .flat()
      .filter((x) => x.EncryptionKey !== null)
      .map((x) => x.EncryptionKey as IKey);
    const oldIds = new Set(keys.map((item) => item.Id));
    const onlyNewKeys = newKeys.filter((item) => !oldIds.has(item.Id));
    setKeys([...keys, ...onlyNewKeys]);

    setChats(packs.flat());
    setIsInitialized(true);
  };

  useEffect(() => {
    compileData();
  }, [data]);

  useEffect(() => {
    if (!isInitialized) {
      fetchNextPage();
    }
  }, []);

  useEffect(() => {
    if (!listFilter.initalized) return;

    setTimeout(() => {
      refetch();
    }, 100);
  }, [listFilter]);

  if (isLoading && !isFetchingNextPage) {
    return (
      <div>
        {Array.from({ length: 100 }, (_, index) => (
          <PackageSkeleton key={index} />
        ))}
      </div>
    );
  }

  return (
    <div className={styles.wrapper2}>
      <div className={styles.order}>
        <SortOrder
          isLoading={false}
          refetch={() => {
            // setNextPage(0);
            // setClients([]);
            // setTimeout(() => {
            //   refetch();
            // }, 100);
          }}
          setListFilter={setListFilter}
          type={listFilter.type}
          order={listFilter.order}
        />
      </div>
      <div className={styles.issueList}>
        <List
          ref={scrollWrapper}
          onScroll={() => {
            handleLoadNextPage();
          }}
          sx={{
            width: "100%",
            position: "relative",
            overflow: "auto",
            maxHeight: "calc(100% )",
            "& ul": { padding: 0 },
          }}
        >
          {!chats.length &&
            !isLoading &&
            !isFetchingNextPage &&
            isInitialized && (
              <div style={{ height: "calc(100vh - 180px)" }}>
                <NotFoundScreen text={t("emptyStates.noResultsSuffix")} />
              </div>
            )}
          {chats.map((pack) => {
            return (
              <div key={pack.Id} className={styles.packe}>
                <ChatCard
                  scrollWrapper={scrollWrapper}
                  key={pack.Id}
                  onPreview={() => {}}
                  isFirst={false}
                  selectedElements={selectedElements}
                  setSelectedElements={setSelectedElements}
                  isSelectable={true}
                  selectMultiple={false}
                  keys={keys}
                  msg={pack}
                  statuses={statuses}
                  tags={tags}
                  decodedContent={undefined}
                />
              </div>
            );
          })}
          <div ref={separatorRef} style={{ height: "5px" }}></div>
        </List>
      </div>
    </div>
  );
};
